/**
 * MUI ActionBar Component
 * 
 * This component is the Material UI equivalent of the original ActionBar component.
 * It renders a bar of action buttons for the Conference Room Designer.
 */

import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Box, Typography, Tooltip, useTheme } from '@mui/material';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { MuiButtonGroup, MuiUnitSwitch } from '../molecules';
import ToolContext from 'components/services/ToolContext';
import { MuiZoomButton } from '../molecules';
import { MuiUndoButton } from '../molecules';
import ReducersIndex from 'reduxModules/ducks';
import { ActionText } from 'globalConstants/Icons/Actions';
import { getCurrentView } from 'reduxModules/ducks/ui';
import { getArea, getAvailableActions, getCurrentValue2 } from 'reduxModules/selectors/containerSelectors';
import { isEmpty, trimEnd } from 'lodash';
import { ActionBarProps } from 'types/components/organisms';
import { AnyAction } from '@reduxjs/toolkit';
import type { RootState } from 'types/redux/state';

/**
 * MUI ActionBar component
 * 
 * @param props - Component props
 * @returns The MUI ActionBar component
 */
const MuiActionBar: React.FC<ActionBarProps> = (props) => {
  const theme = useTheme();
  
  // State and selector hooks
  const currentView = useSelector(getCurrentView as (state: any) => string); // active menu
  const showing = useSelector((state: any) => (getCurrentValue2 as any)(state, 'showing')); // container or area 
  const spotlightTarget = useSelector((state: any) => state.ui.spotlightTarget); // walkthrough guide mode 
  
  // We need to cast ToolContext to any since we don't have its type definition yet
  const toolContext = useContext(ToolContext) as any;
  const { actions: act, actionConditions: conditions } = toolContext;

  const currentDevice = useSelector((state: any) => (getCurrentValue2 as any)(state, 'currentDevice'));

  // Memoize the items array to prevent unnecessary recalculations
  const items = React.useMemo(() => {
    if (currentView === 'acoustics' && currentDevice && act?.acoustics?.[currentDevice]) {
      // Use device-specific actions for acoustics view
      return act.acoustics[currentDevice];
    } else {
      // Use normal actions for other views
      return act?.[currentView]?.[showing] ?? [];
    }
  }, [currentView, currentDevice, act, showing]);

  // Memoize the selector params to prevent recreating the object on each render
  const selectorParams = useMemo(() => ({ items, conditions }), [items, conditions]);
  const actions = useSelector((state: any) => (getAvailableActions as any)(state, selectorParams));

  const dispatch = useDispatch();
  const store = useStore();
  const button = useSelector((state: RootState) => state.ui.action);
  const [buttonActive, setButtonActive] = useState<string>(button);
  const [toolTip, setToolTip] = useState<string>('');
  const [delay, setDelay] = useState<string>('0s');

  // Effect to sync buttonActive with Redux state
  useEffect(() => {
    if (items.includes(button) && buttonActive !== button) {
      setButtonActive(button);
    }
  }, [button, items, buttonActive]);

  // Separate effect for handling empty button state
  useEffect(() => {
    if (button === '' && buttonActive && items.includes(buttonActive)) {
      // Only dispatch if we have a valid buttonActive and no current button set
      dispatch(ReducersIndex.uiReducers.setAction(buttonActive) as unknown as AnyAction);
    }
  }, [button, buttonActive, items, dispatch]);

  // Event handlers
  const handleEnter = (evt: React.MouseEvent, item: string): void => {
    const actionTextFunction = (ActionText as Record<string, (device: string) => string>)[item];
    if (actionTextFunction) {
      setToolTip(actionTextFunction(trimEnd(currentDevice, 's')));
    }
    setDelay('0s');
  };

  const handleLeave = (): void => setDelay('.5s');

  const handleClick = (evt: React.MouseEvent, item: string): void => {
    setButtonActive(item);
    setDelay('2.5s');
    // Cast to AnyAction since we don't have exact action type
    dispatch(ReducersIndex.uiReducers.setAction(item) as unknown as AnyAction);
  };

  // Helper functions
  const getState = (item: string): string => buttonActive === item ? 'button_selected' : 'button_disabled';
  const checked = (item: string): boolean => buttonActive === item;

  // Context and selectors for conditional rendering
  const hasContainer = (toolContext.constants.tool.containerName as boolean);
  const inArea = useSelector((state: any) => (getArea as any)(state));
  const showUndo = useSelector((state: RootState) => {
    const customizedDevicesObject = state.container.areas[state.container.activeTab]?.customized;
    /**
     * @note: The current implementation of CRD uses a single camera so the menu item is 'camera' while the object is ready to support
     * multiple cameras. Once that feature is enables and the menu item (label) changes to 'cameras' this override will not be necesary
     */
    if (isEmpty(customizedDevicesObject)) return false;

    // PIG-308: undo also clears expansion mic/speaker customisation (see resetArea
    // in conferenceroomDesigner/logic.js). The button needs to surface when ONLY
    // an expansion device is customised — without this the user could pin an
    // expansion mic/speaker, leave it dirty, and never see an undo affordance.
    //
    // For acoustics view, show undo if any primary or expansion mic/speaker is
    // customised (acoustics view displays both device classes).
    // PIG-620: cameras are also movable in acoustics, so a customised camera must
    // surface the undo affordance too — otherwise moving the camera here left the
    // user with no way to undo (mic/speaker moves already showed it).
    if (currentView === 'acoustics') {
      return Boolean(
        customizedDevicesObject.microphones
        || customizedDevicesObject.speakers
        || customizedDevicesObject.cameras
        || customizedDevicesObject.expansionMicrophones
        || customizedDevicesObject.expansionSpeakers,
      );
    }

    // For per-device views, accept either the primary customised flag or the
    // matching expansion one. `currentMenu` is the view's device-class name
    // ('microphones' / 'speakers' / 'cameras'); the expansion-flag key follows
    // a fixed naming pattern so the lookup is direct.
    const currentMenu = `${currentView}${currentView === 'camera' ? 's' : ''}`;
    const expansionKey = currentMenu === 'microphones' ? 'expansionMicrophones'
      : currentMenu === 'speakers' ? 'expansionSpeakers'
      : null;
    return Object.entries(customizedDevicesObject).some(([key, value]) => {
      if (value !== true) return false;
      if (hasContainer) return true;
      return key === currentMenu || key === expansionKey;
    });
  });

  // Track undo button pressed state
  const [undoPressed, setUndoPressed] = useState(false);

  // Return null if no actions are available
  if (!actions || actions.length === 0) {
    return null;
  }

  // Create tooltips mapping for ButtonGroup
  const tooltips = (actions || []).reduce((acc: Record<string, string>, item: string) => {
    const actionTextFunction = (ActionText as Record<string, (device: string) => string>)[item];
    if (actionTextFunction) {
      acc[item] = actionTextFunction(trimEnd(currentDevice, 's'));
    }
    return acc;
  }, {});

  const enhancedProps = {
    ...props,
    items: actions,
    handleClick: handleClick,
    handleEnter: handleEnter,
    handleLeave: handleLeave,
    getState: getState,
    checked: checked,
    tooltips: tooltips,
  };

  // Function to get the correct SVG icon for each action
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'measure':
        return (
          <g transform="translate(24, 16) scale(1.3) translate(-12, -8)">
            <path d="M29.6875 12.2998C30.5329 12.2998 31.2 12.9935 31.2002 13.8252V17.9521C31.2002 18.784 30.533 19.4775 29.6875 19.4775H18.2227C17.8456 19.4775 17.4835 19.3281 17.2168 19.0615C16.9834 18.8282 16.839 18.5218 16.8066 18.1963L16.7998 18.0557V13.8252C16.8 12.9935 17.4671 12.2998 18.3125 12.2998L29.6875 12.2998ZM18.3125 13.7002C18.2611 13.7002 18.2004 13.7458 18.2002 13.8252V18.0557L18.2061 18.0713C18.2102 18.0755 18.2168 18.0781 18.2227 18.0781H29.6875C29.739 18.0781 29.7998 18.0318 29.7998 17.9521V13.8252C29.7996 13.7458 29.7389 13.7002 29.6875 13.7002H28.833V15.167C28.8328 15.443 28.609 15.667 28.333 15.667C28.0571 15.6668 27.8332 15.4429 27.833 15.167V13.7002H26.667V14.4443C26.667 14.7204 26.443 14.9442 26.167 14.9443C25.8909 14.9443 25.667 14.7205 25.667 14.4443V13.7002H24.5V15.167C24.4998 15.443 24.276 15.667 24 15.667C23.724 15.667 23.5002 15.443 23.5 15.167V13.7002H22.333V14.4443C22.333 14.7205 22.1092 14.9443 21.833 14.9443C21.557 14.9442 21.333 14.7204 21.333 14.4443V13.7002H20.167V15.167C20.1668 15.4429 19.9429 15.6668 19.667 15.667C19.391 15.667 19.1672 15.443 19.167 15.167V13.7002H18.3125Z" fill={theme.palette.text.primary} transform="translate(-12, -8)" vectorEffect="non-scaling-stroke"/>
          </g>
        );
      case 'armedghostchair':
      case 'ghostchair':
        return (
          <g transform="translate(24, 16) scale(1.5) translate(-6.5, -7.5)">
            <path d="M7.75 0.700195C8.19183 0.700195 8.5498 1.05817 8.5498 1.5L8.5498 14.25L8.5459 14.332C8.50481 14.7353 8.16413 15.0498 7.75 15.0498C7.33587 15.0498 6.99519 14.7353 6.9541 14.332L6.9502 14.25L6.9502 10.792H4.86621L3.63574 13.8486L3.61816 13.9355C3.60963 13.9641 3.59877 13.9922 3.58496 14.0186L3.53711 14.0918C3.51874 14.1146 3.498 14.1357 3.47559 14.1543L3.4043 14.2021C3.37906 14.2156 3.35258 14.2265 3.3252 14.2344L3.24121 14.249L3.15625 14.2461C3.10012 14.2381 3.04587 14.2179 2.99707 14.1875C2.97267 14.1723 2.94968 14.1547 2.92871 14.1348L2.87207 14.0684C2.85506 14.0441 2.84017 14.0184 2.82812 13.9912L2.7998 13.9072C2.78633 13.8489 2.78393 13.7878 2.79297 13.7285L3.25879 10.792C3.12239 10.4793 3.06514 10.1366 2.7666 10.0098C2.74057 9.9987 2.71574 9.98492 2.69238 9.96875L2.62695 9.91309C2.60698 9.89226 2.58889 9.86917 2.57324 9.84473L2.5332 9.7666C2.51146 9.71166 2.50001 9.65233 2.5 9.59277C2.5 9.5332 2.51146 9.4739 2.5332 9.41895C2.55495 9.36411 2.58691 9.31421 2.62695 9.27246C2.667 9.23077 2.71446 9.19796 2.7666 9.17578C2.81889 9.15357 2.8753 9.14271 2.93164 9.14355H3.67578L3.75195 9.13672L3.81348 9.14355H6.9502V8.6748H5.5C4.43686 8.6748 3.57521 7.81313 3.5752 6.75L3.5752 3.4502C3.5752 2.75984 4.13484 2.2002 4.8252 2.2002L6.9502 2.2002V1.5C6.9502 1.05817 7.30817 0.700195 7.75 0.700195ZM5.1748 3.7998L5.1748 6.75C5.17482 6.92948 5.32052 7.07519 5.5 7.0752L6.9502 7.0752L6.9502 3.7998H5.1748Z" fill={theme.palette.text.primary} opacity="0.4" />
            <path d="M11.8242 9.14355H12.5684C12.6247 9.14271 12.6811 9.15357 12.7334 9.17578C12.7855 9.19796 12.833 9.23077 12.873 9.27246C12.9131 9.31421 12.9451 9.36411 12.9668 9.41895C12.9885 9.4739 13 9.5332 13 9.59277C13 9.65233 12.9885 9.71166 12.9668 9.7666L12.9268 9.84473C12.9111 9.86917 12.893 9.89226 12.873 9.91309L12.8076 9.96875C12.7843 9.98492 12.7594 9.9987 12.7334 10.0098C12.4349 10.1366 12.3776 10.4793 12.2412 10.792L12.707 13.7285C12.7161 13.7878 12.7137 13.8489 12.7002 13.9072L12.6719 13.9912C12.6598 14.0184 12.6449 14.0441 12.6279 14.0684L12.5713 14.1348C12.5503 14.1547 12.5273 14.1723 12.5029 14.1875C12.4541 14.2179 12.3999 14.2381 12.3437 14.2461L12.2588 14.249L12.1748 14.2344C12.1474 14.2265 12.1209 14.2156 12.0957 14.2021L12.0244 14.1543C12.002 14.1357 11.9813 14.1146 11.9629 14.0918L11.915 14.0186C11.9012 13.9922 11.8904 13.9641 11.8818 13.9355L11.8643 13.8486L10.6338 10.792H7.375V9.14355H11.6865L11.748 9.13672L11.8242 9.14355ZM10.6748 2.2002C11.3652 2.2002 11.9248 2.75984 11.9248 3.4502V6.75C11.9248 7.81313 11.0631 8.6748 10 8.6748H7.75V7.0752H10C10.1795 7.07519 10.3252 6.92948 10.3252 6.75V3.7998H7.75V2.2002L10.6748 2.2002Z" fill={theme.palette.text.primary} opacity="0.4" />
          </g>
        );
      case 'block':
        return (
          <g transform="translate(24, 16) scale(1.5) translate(-8, -8)">
            <path d="M8.09961 1.5C11.7447 1.5 14.6992 4.45453 14.6992 8.09961C14.6992 11.7447 11.7447 14.6992 8.09961 14.6992C4.45453 14.6992 1.5 11.7447 1.5 8.09961C1.5 4.45453 4.45453 1.5 8.09961 1.5ZM4.04395 5.1748C3.44981 5.99722 3.09961 7.0075 3.09961 8.09961C3.09961 10.861 5.33819 13.0996 8.09961 13.0996C9.19164 13.0996 10.2011 12.7484 11.0234 12.1543L4.04395 5.1748ZM8.09961 3.09961C7.0075 3.09961 5.99722 3.44981 5.1748 4.04395L12.1543 11.0234C12.7484 10.2011 13.0996 9.19164 13.0996 8.09961C13.0996 5.33819 10.861 3.09961 8.09961 3.09961Z" fill={theme.palette.text.primary}/>
          </g>
        );
      case 'position':
        return (
          <g transform="translate(24, 16) scale(1.5) translate(-8, -8)">
            <path fillRule="evenodd" clipRule="evenodd" d="M6.01965 5.35344C6.54475 4.82833 7.25694 4.53333 7.99955 4.53333C8.74215 4.53333 9.45434 4.82833 9.97945 5.35344C10.5045 5.87854 10.7995 6.59073 10.7995 7.33333C10.7995 8.07594 10.5045 8.78813 9.97945 9.31323C9.45434 9.83834 8.74215 10.1333 7.99955 10.1333C7.25694 10.1333 6.54475 9.83834 6.01965 9.31323C5.49455 8.78813 5.19955 8.07594 5.19955 7.33333C5.19955 6.59073 5.49455 5.87854 6.01965 5.35344ZM7.99955 6.13333C7.68129 6.13333 7.37606 6.25976 7.15102 6.48481C6.92598 6.70985 6.79955 7.01507 6.79955 7.33333C6.79955 7.65159 6.92598 7.95682 7.15102 8.18186C7.37606 8.40691 7.68129 8.53333 7.99955 8.53333C8.31781 8.53333 8.62303 8.40691 8.84808 8.18186C9.07312 7.95682 9.19955 7.65159 9.19955 7.33333C9.19955 7.01507 9.07312 6.70985 8.84808 6.48481C8.62303 6.25976 8.31781 6.13333 7.99955 6.13333Z" fill={theme.palette.text.primary}/>
            <path fillRule="evenodd" clipRule="evenodd" d="M7.99955 2.8002C7.10295 2.8002 6.22648 3.06606 5.48099 3.56418C4.73549 4.0623 4.15444 4.7703 3.81132 5.59864C3.46819 6.42699 3.3784 7.33848 3.5533 8.21785C3.7282 9.0972 4.15991 9.90494 4.79385 10.5389L7.62295 13.3674C7.72295 13.4673 7.85853 13.5234 7.99988 13.5234C8.14124 13.5234 8.27681 13.4673 8.37681 13.3674L11.2052 10.539C11.8391 9.90499 12.2709 9.09722 12.4458 8.21785C12.6207 7.33848 12.5309 6.42699 12.1878 5.59864C11.8447 4.7703 11.2636 4.0623 10.5181 3.56418C9.77261 3.06606 8.89615 2.8002 7.99955 2.8002ZM4.59208 2.23383C5.6007 1.5599 6.7865 1.2002 7.99955 1.2002C9.21259 1.2002 10.3984 1.5599 11.407 2.23383C12.4156 2.90775 13.2018 3.86563 13.666 4.98633C14.1302 6.10703 14.2517 7.34023 14.0151 8.52997C13.7784 9.71971 13.1943 10.8126 12.3366 11.6703L9.5079 14.499C9.10788 14.8986 8.5653 15.1234 7.99988 15.1234C7.43446 15.1234 6.89216 14.8989 6.49215 14.4993L3.6626 11.6704C2.80486 10.8126 2.22067 9.71971 1.98404 8.52997C1.74741 7.34023 1.86889 6.10703 2.33312 4.98633C2.79734 3.86563 3.58347 2.90775 4.59208 2.23383Z" fill={theme.palette.text.primary}/>
          </g>
        );
      case 'level':
        // For speakers, level should show 4-way arrows (move icon)
        // For microphones/cameras, level should show horizontal arrows (distance icon)
        if (currentDevice === 'speakers') {
          return (
            <g transform="translate(24, 16) scale(1.5) translate(-8, -8)">
              <path fillRule="evenodd" clipRule="evenodd" d="M7.43451 1.43433C7.74693 1.12191 8.25346 1.12191 8.56588 1.43433L10.5659 3.43433C10.8783 3.74675 10.8783 4.25328 10.5659 4.5657C10.2535 4.87812 9.74693 4.87812 9.43451 4.5657L8.8002 3.93138V6.00001C8.8002 6.44184 8.44202 6.80001 8.0002 6.80001C7.55837 6.80001 7.2002 6.44184 7.2002 6.00001L7.2002 3.93138L6.56588 4.5657C6.25346 4.87812 5.74693 4.87812 5.43451 4.5657C5.12209 4.25328 5.12209 3.74675 5.43451 3.43433L7.43451 1.43433ZM4.56588 5.43433C4.8783 5.74675 4.8783 6.25328 4.56588 6.5657L3.93157 7.20001L6.0002 7.20001C6.44202 7.20001 6.8002 7.55818 6.8002 8.00001C6.8002 8.44184 6.44202 8.80001 6.0002 8.80001H3.93157L4.56588 9.43433C4.8783 9.74675 4.8783 10.2533 4.56588 10.5657C4.25346 10.8781 3.74693 10.8781 3.43451 10.5657L1.43451 8.5657C1.12209 8.25328 1.12209 7.74675 1.43451 7.43433L3.43451 5.43433C3.74693 5.12191 4.25346 5.12191 4.56588 5.43433ZM11.4345 5.43433C11.7469 5.12191 12.2535 5.12191 12.5659 5.43433L14.5659 7.43433C14.8783 7.74675 14.8783 8.25328 14.5659 8.5657L12.5659 10.5657C12.2535 10.8781 11.7469 10.8781 11.4345 10.5657C11.1221 10.2533 11.1221 9.74675 11.4345 9.43433L12.0688 8.80001H10.0002C9.55837 8.80001 9.2002 8.44184 9.2002 8.00001C9.2002 7.55818 9.55837 7.20001 10.0002 7.20001H12.0688L11.4345 6.5657C11.1221 6.25328 11.1221 5.74675 11.4345 5.43433ZM7.2002 12.0686V10C7.2002 9.55818 7.55837 9.20001 8.0002 9.20001C8.44202 9.20001 8.8002 9.55818 8.8002 10V12.0686L9.43451 11.4343C9.74693 11.1219 10.2535 11.1219 10.5659 11.4343C10.8783 11.7467 10.8783 12.2533 10.5659 12.5657L8.56588 14.5657C8.25346 14.8781 7.74693 14.8781 7.43451 14.5657L5.43451 12.5657C5.12209 12.2533 5.12209 11.7467 5.43451 11.4343C5.74693 11.1219 6.25346 11.1219 6.56588 11.4343L7.2002 12.0686Z" fill={theme.palette.text.primary}/>
            </g>
          );
        } else {
          return (
            <g transform="translate(24, 16) scale(1.5) translate(-8, -8)">
              <path d="M11.9346 5.43457C12.247 5.12215 12.753 5.12215 13.0654 5.43457L15.0654 7.43457C15.3778 7.74699 15.3778 8.25302 15.0654 8.56543L13.0654 10.5654C12.753 10.8778 12.247 10.8778 11.9346 10.5654C11.6222 10.253 11.6222 9.74699 11.9346 9.43457L12.5693 8.79981L3.93066 8.79981L4.56543 9.43457L4.62012 9.49512C4.87655 9.80934 4.85838 10.2725 4.56543 10.5654C4.27248 10.8584 3.80933 10.8765 3.49512 10.6201L3.43457 10.5654L1.43457 8.56543C1.4002 8.53106 1.37056 8.49362 1.34375 8.45508C1.32624 8.42988 1.30949 8.40415 1.29492 8.37695C1.29044 8.36859 1.28639 8.36005 1.28223 8.35156C1.25247 8.29092 1.22948 8.2263 1.21582 8.1582C1.21485 8.15338 1.21475 8.14839 1.21387 8.14356C1.20541 8.09694 1.2002 8.04906 1.2002 8C1.2002 7.95063 1.20531 7.90236 1.21387 7.85547C1.21915 7.82663 1.22493 7.79776 1.2334 7.76953C1.24611 7.72716 1.263 7.68656 1.28223 7.64746C1.31999 7.57063 1.37064 7.49851 1.43457 7.43457L3.43457 5.43457C3.74699 5.12215 4.25301 5.12215 4.56543 5.43457C4.87782 5.74699 4.87784 6.25302 4.56543 6.56543L3.93066 7.2002L12.5693 7.2002L11.9346 6.56543C11.6222 6.25302 11.6222 5.74699 11.9346 5.43457Z" fill={theme.palette.text.primary}/>
            </g>
          );
        }
      case 'relativelevel':
        return (
          <g transform="translate(24, 16) scale(1.5) translate(-8, -8)">
            <path d="M8 2.2002C8.44183 2.2002 8.7998 2.55817 8.7998 3V12C8.7998 12.4418 8.44183 12.7998 8 12.7998C7.55817 12.7998 7.2002 12.4418 7.2002 12L7.2002 3C7.2002 2.55817 7.55817 2.2002 8 2.2002ZM5 4.2002C5.44183 4.2002 5.7998 4.55817 5.7998 5L5.7998 11C5.7998 11.4418 5.44183 11.7998 5 11.7998C4.55817 11.7998 4.2002 11.4418 4.2002 11L4.2002 5C4.2002 4.55817 4.55817 4.2002 5 4.2002ZM11 4.2002C11.4418 4.2002 11.7998 4.55817 11.7998 5L11.7998 11C11.7998 11.4418 11.4418 11.7998 11 11.7998C10.5582 11.7998 10.2002 11.4418 10.2002 11L10.2002 5C10.2002 4.55817 10.5582 4.2002 11 4.2002ZM2 5.7002C2.44183 5.7002 2.7998 6.05817 2.7998 6.5L2.7998 8.5C2.7998 8.94183 2.44183 9.2998 2 9.2998C1.55817 9.2998 1.2002 8.94183 1.2002 8.5L1.2002 6.5C1.2002 6.05817 1.55817 5.7002 2 5.7002ZM14 5.7002C14.4418 5.7002 14.7998 6.05817 14.7998 6.5V8.5C14.7998 8.94183 14.4418 9.2998 14 9.2998C13.5582 9.2998 13.2002 8.94183 13.2002 8.5V6.5C13.2002 6.05817 13.5582 5.7002 14 5.7002Z" fill={theme.palette.text.primary}/>
          </g>
        );
      case 'distance':
        return (
          <g transform="translate(24, 16) scale(1.5) translate(-8, -8)">
            <path d="M11.9336 5.43463C12.246 5.12221 12.752 5.12221 13.0645 5.43463L15.0645 7.43463C15.3768 7.74705 15.3769 8.25308 15.0645 8.56549L13.0645 10.5655C12.752 10.8779 12.246 10.8779 11.9336 10.5655C11.6212 10.2531 11.6212 9.74705 11.9336 9.43463L12.5684 8.79987L3.92969 8.79987L4.56445 9.43463L4.61914 9.49518C4.87557 9.8094 4.8574 10.2725 4.56445 10.5655C4.2715 10.8584 3.80835 10.8766 3.49414 10.6202L3.43359 10.5655L1.43359 8.56549C1.39922 8.53112 1.36958 8.49368 1.34277 8.45514C1.32527 8.42994 1.30851 8.40421 1.29395 8.37701C1.28947 8.36865 1.28541 8.36011 1.28125 8.35162C1.2515 8.29098 1.2285 8.22636 1.21484 8.15826C1.21387 8.15344 1.21377 8.14845 1.21289 8.14362C1.20444 8.097 1.19922 8.04912 1.19922 8.00006C1.19922 7.95069 1.20433 7.90242 1.21289 7.85553C1.21817 7.82669 1.22396 7.79782 1.23242 7.76959C1.24513 7.72722 1.26202 7.68662 1.28125 7.64752C1.31901 7.57069 1.36966 7.49857 1.43359 7.43463L3.43359 5.43463C3.74601 5.12221 4.25203 5.12221 4.56445 5.43463C4.87684 5.74705 4.87686 6.25308 4.56445 6.56549L3.92969 7.20026L12.5684 7.20026L11.9336 6.56549C11.6212 6.25308 11.6212 5.74705 11.9336 5.43463Z" fill={theme.palette.text.primary}/>
          </g>
        );
      case 'move':
        return (
          <g transform="translate(24, 16) scale(1.5) translate(-8, -8)">
            <path fillRule="evenodd" clipRule="evenodd" d="M7.43353 1.43433C7.74595 1.12191 8.25248 1.12191 8.5649 1.43433L10.5649 3.43433C10.8773 3.74675 10.8773 4.25328 10.5649 4.5657C10.2525 4.87812 9.74595 4.87812 9.43353 4.5657L8.79922 3.93138V6.00001C8.79922 6.44184 8.44105 6.80001 7.99922 6.80001C7.55739 6.80001 7.19922 6.44184 7.19922 6.00001L7.19922 3.93138L6.5649 4.5657C6.25248 4.87812 5.74595 4.87812 5.43353 4.5657C5.12111 4.25328 5.12111 3.74675 5.43353 3.43433L7.43353 1.43433ZM4.5649 5.43433C4.87732 5.74675 4.87732 6.25328 4.5649 6.5657L3.93059 7.20001L5.99922 7.20001C6.44105 7.20001 6.79922 7.55818 6.79922 8.00001C6.79922 8.44184 6.44105 8.80001 5.99922 8.80001H3.93059L4.5649 9.43433C4.87732 9.74675 4.87732 10.2533 4.5649 10.5657C4.25248 10.8781 3.74595 10.8781 3.43353 10.5657L1.43353 8.5657C1.12111 8.25328 1.12111 7.74675 1.43353 7.43433L3.43353 5.43433C3.74595 5.12191 4.25248 5.12191 4.5649 5.43433ZM11.4335 5.43433C11.746 5.12191 12.2525 5.12191 12.5649 5.43433L14.5649 7.43433C14.8773 7.74675 14.8773 8.25328 14.5649 8.5657L12.5649 10.5657C12.2525 10.8781 11.746 10.8781 11.4335 10.5657C11.1211 10.2533 11.1211 9.74675 11.4335 9.43433L12.0678 8.80001H9.99922C9.55739 8.80001 9.19922 8.44184 9.19922 8.00001C9.19922 7.55818 9.55739 7.20001 9.99922 7.20001H12.0678L11.4335 6.5657C11.1211 6.25328 11.1211 5.74675 11.4335 5.43433ZM7.19922 12.0686V10C7.19922 9.55818 7.55739 9.20001 7.99922 9.20001C8.44105 9.20001 8.79922 9.55818 8.79922 10V12.0686L9.43353 11.4343C9.74595 11.1219 10.2525 11.1219 10.5649 11.4343C10.8773 11.7467 10.8773 12.2533 10.5649 12.5657L8.5649 14.5657C8.25248 14.8781 7.74595 14.8781 7.43353 14.5657L5.43353 12.5657C5.12111 12.2533 5.12111 11.7467 5.43353 11.4343C5.74595 11.1219 6.25248 11.1219 6.5649 11.4343L7.19922 12.0686Z" fill={theme.palette.text.primary}/>
          </g>
        );
      case 'add':
        return (
          <g transform="translate(24, 16) scale(1.2) translate(-12, -12)">
            <line x1="12" y1="6" x2="12" y2="18" stroke={theme.palette.text.primary} strokeWidth="3"/>
            <line x1="6" y1="12" x2="18" y2="12" stroke={theme.palette.text.primary} strokeWidth="3"/>
          </g>
        );
      case 'remove':
      case 'delete':
        return (
          <g transform="translate(24, 16) scale(1.2) translate(-12, -12)">
            <line x1="6" y1="12" x2="18" y2="12" stroke={theme.palette.text.primary} strokeWidth="3"/>
          </g>
        );
      case 'listen':
        return (
          <g transform="translate(24, 16) scale(1.5) translate(-6, -7)">
            <path d="M2.66667 12.6526C2.86 12.6526 3.04 12.6121 3.17333 12.5515C3.64667 12.3022 3.98 11.9586 4.31333 10.948C4.65333 9.89702 5.29333 9.4052 5.90667 8.92685C6.43333 8.51588 6.98 8.09143 7.45333 7.22233C7.80667 6.57555 8 5.86814 8 5.24158C8 3.35515 6.53333 1.87295 4.66667 1.87295C3.02511 1.87295 1.69289 3.01922 1.39545 4.57864C1.32647 4.94031 1.03486 5.24158 0.666666 5.24158C0.298477 5.24158 -0.00484276 4.94147 0.0439034 4.57652C0.353921 2.2555 2.27052 0.525505 4.66667 0.525505C7.28667 0.525505 9.33333 2.59384 9.33333 5.24158C9.33333 6.09047 9.08 7.02695 8.62 7.8691C8.01333 8.98075 7.3 9.53994 6.72 9.99134C6.18 10.409 5.79333 10.7122 5.58 11.3725C5.18 12.5987 4.66667 13.2859 3.76 13.7642C3.42 13.9192 3.04667 14 2.66667 14C1.41875 14 0.371707 13.1348 0.0805302 11.9653C-0.00842476 11.608 0.298477 11.3051 0.666666 11.3051C1.03486 11.3051 1.31986 11.6209 1.49438 11.9451C1.72072 12.3655 2.16214 12.6526 2.66667 12.6526ZM9.35007 1.45437C9.12015 1.16561 9.11612 0.745022 9.37574 0.482646C9.63869 0.216914 10.0706 0.215173 10.3082 0.503831C11.3649 1.78784 12 3.43904 12 5.24158C12 7.0451 11.3642 8.69712 10.3064 9.98143C10.0697 10.2688 9.63955 10.2671 9.37766 10.0024C9.1202 9.74226 9.12288 9.32559 9.34992 9.03848C10.1717 7.99926 10.6667 6.67984 10.6667 5.24158C10.6667 3.80584 10.1735 2.48853 9.35007 1.45437ZM6.33333 5.24158C6.33333 6.17132 5.58667 6.92589 4.66667 6.92589C3.74667 6.92589 3 6.17132 3 5.24158C3 4.31184 3.74667 3.55727 4.66667 3.55727C5.58667 3.55727 6.33333 4.31184 6.33333 5.24158Z" fill={theme.palette.text.primary}/>
          </g>
        );
      default:
        return <circle cx="24" cy="16" r="8" fill={theme.palette.text.primary} />;
    }
  };

  // Get the undo icon for SVG rendering - using undo icon path with theme color
  const getUndoIcon = () => (
    <g transform="translate(24, 16) scale(0.025) translate(-480, 480)">
      <path d="M320-200q-17 0-28.5-11.5T280-240q0-17 11.5-28.5T320-280h244q63 0 109.5-40T720-420q0-60-46.5-100T564-560H312l76 76q11 11 11 28t-11 28q-11 11-28 11t-28-11L188-572q-6-6-8.5-13t-2.5-15q0-8 2.5-15t8.5-13l144-144q11-11 28-11t28 11q11 11 11 28t-11 28l-76 76h252q97 0 166.5 63T800-420q0 94-69.5 157T564-200H320Z"
            fill={theme.palette.text.primary}/>
    </g>
  );

  // Handle undo button click - reset current device section to defaults
  const handleUndoClick = (e: React.MouseEvent<SVGSVGElement>) => {
    // First clear any existing errors that might interfere with recalculation
    // Clear error 130 specifically by calling setError with just the code
    dispatch(ReducersIndex.uiReducers.setError({ code: '130' }) as unknown as AnyAction);
    
    // Use the proper resetArea action which calls the tool's resetArea logic
    // This will call resetDevices(true, false, false) for microphones as per the git commit
    dispatch(ReducersIndex.container.resetArea(currentDevice) as unknown as AnyAction);
    
    // After clearing customization, force recalculation to restore default devices
    setTimeout(() => {
      // Only use setRecalculate - setRemap causes issues with malformed device structure
      dispatch(ReducersIndex.container.setRecalculate({ flag: true }) as unknown as AnyAction);
    }, 100);
  };

  // Render all available action buttons vertically
  return (
    <Box
      id="actionButtons"
      data-testid="action-buttons-container"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {/* Render regular action buttons */}
      {actions.map((action, index) => (
          <Tooltip 
          key={action}
          title={spotlightTarget ? '' : tooltips[action] || ''}
          placement="right"
          arrow
          enterDelay={500}
          leaveDelay={200}
          disableHoverListener={Boolean(spotlightTarget)}
          disableFocusListener={Boolean(spotlightTarget)}
          disableTouchListener={Boolean(spotlightTarget)}
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                fontSize: '14px',
                fontFamily: '"Open Sans", sans-serif',
                fontWeight: 400,
                padding: '8px 12px',
                borderRadius: '4px',
                border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[200]}`,
                boxShadow: theme.shadows[1],
                '& .MuiTooltip-arrow': {
                  color: theme.palette.background.paper,
                  '&::before': {
                    border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[200]}`,
                  },
                },
              },
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              onClick={(e: React.MouseEvent<SVGSVGElement>) => {
                const buttonEvent = e as unknown as React.MouseEvent<HTMLButtonElement>;
                handleClick(buttonEvent, action);
              }}
              onMouseEnter={(e) => handleEnter(e, action)}
              onMouseLeave={handleLeave}
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                transform: buttonActive === action ? 'scale(1.08)' : 'scale(1)',
                position: 'relative',
                zIndex: 1,
                shapeRendering: 'geometricPrecision',
                WebkitFontSmoothing: 'antialiased',
              }}
              onMouseOver={(e) => {
                if (buttonActive !== action) {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseOut={(e) => {
                if (buttonActive !== action) {
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              <g filter={buttonActive === action ? "none" : `url(#filter_${action}_${index})`}>
                <path
                  d="M8 6C8 2.68629 10.6863 0 14 0H34C37.3137 0 40 2.68629 40 6V26C40 29.3137 37.3137 32 34 32H14C10.6863 32 8 29.3137 8 26V6Z"
                  fill={theme.palette.mode === 'dark' ? theme.palette.background.paper : "white"}
                />
                <path
                  d="M14 0.5H34C37.0376 0.5 39.5 2.96243 39.5 6V26C39.5 29.0376 37.0376 31.5 34 31.5H14C10.9624 31.5 8.5 29.0376 8.5 26V6C8.5 2.96243 10.9624 0.5 14 0.5Z"
                  stroke={buttonActive === action ? theme.palette.text.primary : theme.palette.mode === 'dark' ? theme.palette.grey[600] : "#D0D0D0"}
                  strokeWidth={buttonActive === action ? "1" : "1"}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              {getActionIcon(action)}
              </g>
            {buttonActive !== action && (
              <defs>
                <filter id={`filter_${action}_${index}`} x="0" y="0" width="48" height="48" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                  <feOffset dy="8"/>
                  <feGaussianBlur stdDeviation="4"/>
                  <feComposite in2="hardAlpha" operator="out"/>
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"/>
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
                </filter>
              </defs>
            )}
          </svg>
          </Box>
        </Tooltip>
      ))}
      
      {/* Render undo button if conditions are met */}
      {showUndo && (
        <Tooltip
          title={spotlightTarget ? '' : 
            currentView === 'microphones' ? 'Click to reset all microphones to defaults and remove added devices' :
            currentView === 'speakers' ? 'Click to reset all speakers to defaults and remove added devices' :
            currentView === 'camera' ? 'Click to reset all cameras to defaults and remove added devices' :
            currentView === 'acoustics' ? 'Click to reset all microphones to defaults and remove added devices' :
            'Reset devices to defaults and remove added devices'
          }
          placement="right"
          arrow
          enterDelay={500}
          leaveDelay={200}
          disableHoverListener={Boolean(spotlightTarget)}
          disableFocusListener={Boolean(spotlightTarget)}
          disableTouchListener={Boolean(spotlightTarget)}
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                fontSize: '14px',
                fontFamily: '"Open Sans", sans-serif',
                fontWeight: 400,
                padding: '8px 12px',
                borderRadius: '4px',
                border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[200]}`,
                boxShadow: theme.shadows[1],
                '& .MuiTooltip-arrow': {
                  color: theme.palette.background.paper,
                  '&::before': {
                    border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[200]}`,
                  },
                },
              },
            },
          }}
        >
          <svg
            key="undo"
            data-testid="undo-button-svg"
            onClick={handleUndoClick}
            onMouseEnter={(e) => handleEnter(e, 'undo')}
            onMouseLeave={handleLeave}
            onMouseDown={() => setUndoPressed(true)}
            onMouseUp={() => setUndoPressed(false)}
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              transform: undoPressed ? 'scale(1.08)' : 'scale(1)',
              shapeRendering: 'geometricPrecision',
              WebkitFontSmoothing: 'antialiased',
            }}
            onMouseOver={(e) => {
              if (!undoPressed) {
                e.currentTarget.style.transform = 'scale(1.05)';
              }
            }}
            onMouseOut={(e) => {
              if (!undoPressed) {
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}
          >
            <g filter={undoPressed ? "none" : "url(#filter0_d_1184_22248)"}>
              <path d="M8 6C8 2.68629 10.6863 0 14 0H34C37.3137 0 40 2.68629 40 6V26C40 29.3137 37.3137 32 34 32H14C10.6863 32 8 29.3137 8 26V6Z" fill={theme.palette.mode === 'dark' ? theme.palette.background.paper : "white"}/>
              <path d="M14 0.5H34C37.0376 0.5 39.5 2.96243 39.5 6V26C39.5 29.0376 37.0376 31.5 34 31.5H14C10.9624 31.5 8.5 29.0376 8.5 26V6C8.5 2.96243 10.9624 0.5 14 0.5Z" stroke={undoPressed ? theme.palette.text.primary : theme.palette.mode === 'dark' ? theme.palette.grey[600] : "#D0D0D0"} strokeWidth={undoPressed ? "1" : "1"} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"/>
              {getUndoIcon()}
            </g>
            {!undoPressed && (
              <defs>
                <filter id="filter0_d_1184_22248" x="0" y="0" width="48" height="48" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="8"/>
                <feGaussianBlur stdDeviation="4"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1184_22248"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1184_22248" result="shape"/>
                </filter>
              </defs>
            )}
          </svg>
        </Tooltip>
      )}
    </Box>
  );
};

export default MuiActionBar;
